import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RouterModule } from '@angular/router';
import { BridgeApiService } from '../services/bridge-api.service';
import { CHAIN_MAP, getNetworkName, getNetworkIcon } from '../utils/chains';
@Component({
  selector: 'app-quote-preview',
  standalone: true,
  imports: [CommonModule, TranslateModule, RouterModule],
  templateUrl: './quote-preview.component.html',
})
export class QuotePreviewComponent {
  @Input() quote: any;
  @Input() signature: string = '';
  @Input() fiatCode = 'USD';
  @Input() fiatRates: Record<string, number> = { USD: 1 };
  @Input() swapStatus: 'idle' | 'approve' | 'pending' | 'success' | 'error' | 'cancelled' = 'idle';
  @Input() swapHash: string | null = null;
  @Input() swapError: string | null = null;
  @Input() validateAlternative?: (route: any) => { disabled: boolean; reasons: string[] };
  @Output() executeRequest = new EventEmitter<void>();

  // 👉 nouvel output pour valider une route alternative
  @Output() selectAlternative = new EventEmitter<any>();
  private _fallbackFullGasWei: bigint = 0n;

  private selectedAltKey: string | null = null;
   private currentQuoteKey: string | null = null; // providerKey de la quote affichée
  @Output() copyToClipboard = new EventEmitter<string>();
  @Input() swapDisabled = false;
  @Input() nativeTokenUsd = 0;

  /** gasPriceWei & fullGasWei injectés par le parent */
  @Input() gasPriceWei: bigint = 0n;   // prix du gas (wei)
  @Input() fullGasWei: bigint = 0n;    // approve + swap pour la route “courante”
  @Output() close = new EventEmitter<void>();

  private norm(s: any): string {
  return String(s ?? '').toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g,'').trim();
}

  // 👇 état d’affichage du tableau
  showAlternatives = false;

  swapLogoUrl: string | null = null;
  showToast = false;
  // 👉 Nouvel Output : on remonte la route sélectionnée au parent
  showTooltip: boolean = false;
  private autoHideTimeout?: any;   // <-- nouveau (5s)
  private tooltipTimeout: any;

  toggleTooltip() {
    this.showTooltip = !this.showTooltip;

    // si on ouvre → auto-fermeture après 3s
    if (this.showTooltip) {
      clearTimeout(this.tooltipTimeout);
      this.tooltipTimeout = setTimeout(() => {
        this.showTooltip = false;
      }, 3000);
    }
  }
  constructor(private translate: TranslateService, private bridgeApi: BridgeApiService) {}
  applyGasFromQuote(q: any) {
    // total gas en wei pour la carte principale (fourni par le back)
    const totalWei = BigInt(q?.totalNetworkFee ?? q?.TotalNetworkFee ?? 0);

    // ❌ NE PAS écraser l’@Input parent (gating) :
    // this.fullGasWei = totalWei;

    // ✅ On garde un fallback d’affichage si l’@Input est absent
    this._fallbackFullGasWei = totalWei;

    // prix USD du token gas (ETH/MATIC/…)
    this.nativeTokenUsd = Number(q?.gasTokenPriceUsd ?? q?.GasTokenPriceUsd ?? 0);

    // fallback: si pas de prix mais on a GasFeeUsd, on en déduit le prix
    if ((!this.nativeTokenUsd || this.nativeTokenUsd === 0) && totalWei > 0n) {
      const feeUsd = Number(q?.gasFeeUsd ?? q?.GasFeeUsd ?? 0);
      if (feeUsd) {
        const feeEth = Number(totalWei) / 1e18;
        this.nativeTokenUsd = feeEth ? (feeUsd / feeEth) : 0;
      }
    }
  }


readonly CHAIN_MAP = CHAIN_MAP;

get networkIconUrl(): string | null {
  const id =
    this.quote?.fromChainId ??
    this.quote?.chainId ??
    this.quote?.action?.fromChainId ??
    this.quote?.estimate?.fromChainId ??
    null;

  return getNetworkIcon(id);
}

get networkName(): string {
  const id =
    this.quote?.fromChainId ??
    this.quote?.chainId ??
    this.quote?.action?.fromChainId ??
    this.quote?.estimate?.fromChainId ??
    null;

  return getNetworkName(id);
}

private getProviderKey(x: any): string {
  return this.norm(
    x?.providerKey ?? x?.ProviderKey ??
    x?.toolKey     ?? x?.ToolKey     ??
    x?.tool        ?? x?.Tool        ??
    x?.source      ?? x?.Source      ??
    x?.executorKey ?? x?.ExecutorKey ??
    x?.providerName?? x?.ProviderName?? // fallback lisible
    x?.name
  );
}



private toNum(v: any): number {
  return typeof v === 'bigint' ? Number(v) : Number(v ?? 0);
}



  getRouteNetworkFee(r: any): { textEth: string; textFiat: string } {
    const toNum = (v: any) => (typeof v === 'bigint' ? Number(v) : Number(v ?? 0));

    // ----- Natif (wei) -----
    const gasAmountWei = toNum(r?.gasAmountWei ?? r?.GasAmountWei);
    const gasPriceWei = toNum(r?.gasPriceWei ?? r?.GasPriceWei);
    const estimatedGas = toNum(r?.estimatedGas ?? r?.EstimatedGas);

    let feeWei = gasAmountWei;
    if (!feeWei && gasPriceWei && estimatedGas) {
      feeWei = gasPriceWei * estimatedGas;
    }

    // Symbole réseau canonisé
    const chainId = Number(
      r?.fromChainId ?? r?.chainId ?? r?.action?.fromChainId ?? this.quote?.fromChainId
    );
    const symbol = this.canonicalNativeSymbol(chainId);

    const feeEth = feeWei ? feeWei / 1e18 : 0;
    const textEth = feeEth ? `${feeEth.toFixed(8)} ${symbol}` : '';

    // ----- Fiat -----
    const rate = this.fiatRates?.[this.fiatCode] || 1;
    let usd = toNum(r?.gasFeeUsd ?? r?.GasFeeUsd);

    if (!usd && feeWei) {
      const routeGasUsd = toNum(r?.gasTokenPriceUsd ?? r?.GasTokenPriceUsd);
      const quoteGasUsd = toNum(this.quote?.gasTokenPriceUsd ?? this.quote?.GasTokenPriceUsd);
      const gasTokenUsd = routeGasUsd || quoteGasUsd || (this.nativeTokenUsd || 0);
      if (gasTokenUsd) usd = (feeWei / 1e18) * gasTokenUsd;
    }

    const textFiat = usd
      ? '≈ ' + (usd * rate).toLocaleString(
        this.fiatCode === 'EUR' ? 'fr-FR' : 'en-US',
        { style: 'currency', currency: this.fiatCode }
      )
      : '';

    return { textEth, textFiat };
  }





  networkNames: Record<number, string> = {
    1: 'Ethereum',
    137: 'Polygon',
    56: 'BNB Smart Chain',
    10: 'Optimism',
    42161: 'Arbitrum',
    43114: 'Avalanche',
    250: 'Fantom'
  };

  getNetworkName(chainId: number | undefined): string {
    if (!chainId) return '';
    return this.networkNames[chainId] || `Chain ${chainId}`;
  }


  altReasons(r: any): string[] {
    if (!this.validateAlternative) return [];
    return this.validateAlternative(r).reasons || [];
  }

  private canonicalNativeSymbol(chainId?: number, raw?: string): string {
    const map: Record<number, string> = { 1: 'ETH', 10: 'ETH', 56: 'BNB', 137: 'MATIC', 42161: 'ETH', 8453: 'ETH' };
    if (chainId && map[chainId]) return map[chainId];
    const up = (raw || '').toUpperCase();
    if (up === 'POL' || up === 'MATIC') return map[137]; // unifie sur MATIC
    return up || 'ETH';
  }


  closePreview() { this.close.emit(); }

  get sellTokenSymbol(): string { return this.quote?.sellTokenSymbol ?? ''; }
  get buyTokenSymbol(): string  { return this.quote?.buyTokenSymbol  ?? ''; }
    private debugAltUI = true;
  private pendingTimeout: any;
ngOnChanges(changes: SimpleChanges) {
  // --- ta logique existante sur la quote ---
  if (changes['quote']) {
    const newKey = this.getProviderKey(this.quote) || null;
    this.currentQuoteKey = newKey;

    if (!this.selectedAltKey) {
      this.selectedAltKey = newKey;              // init
    } else if (newKey && newKey === this.selectedAltKey) {
      /* ok, la quote correspond à la sélection */
    } else {
      /* ne pas écraser la sélection utilisateur */
    }
  }

  // --- timers de statut ---
  // On annule d'abord les timers en cours
  clearTimeout(this.pendingTimeout);
  clearTimeout(this.autoHideTimeout);

  if (this.swapStatus === 'pending') {
    // ⏳ pending : auto-reset à idle au bout de 30s (ton comportement actuel)
    this.pendingTimeout = setTimeout(() => {
      if (this.swapStatus === 'pending') this.swapStatus = 'idle';
    }, 30000);
  } else if (this.swapStatus && this.swapStatus !== 'idle') {
    // ✅ 🔐 ❌ ℹ️ etc. : auto-hide après 5s
    this.autoHideTimeout = setTimeout(() => {
      // sécurité : ne pas écraser un changement intervenu entre-temps
      if (this.swapStatus && this.swapStatus !== 'pending') {
        this.swapStatus = 'idle';
      }
    }, 5000);
  }
}

ngOnDestroy() {
  clearTimeout(this.pendingTimeout);
  clearTimeout(this.autoHideTimeout);
}


  private getRouteKey(r: any): string {
    const raw =
      r?.providerKey ?? r?.ProviderKey ??
      r?.toolKey     ?? r?.ToolKey     ??
      r?.tool        ?? r?.Tool        ??
      r?.source      ?? r?.Source      ??
      r?.providerName?? r?.ProviderName??
      r?.name        ?? '';

    const key = String(raw || '').toLowerCase().trim();

    return key;
  }

  isAltSelected(r: any): boolean {
  const k = this.getProviderKey(r);
  return !!k && (k === this.selectedAltKey || k === this.currentQuoteKey);
}

  // ton test d’exécutabilité (garde tel quel si tu l’as déjà)
  isExecutableRoute(r: any): boolean {
    const tx = r?.transactionRequest ?? r?.TransactionRequest ?? null;
    const hasTx = !!(tx?.to && tx?.data) || !!(r?.to && r?.data) || !!(r?.target && r?.data);
    const hasTyped = !!(r?.domain && r?.types && r?.value);
    return hasTx || hasTyped;
  }

  isAlternativeDisabled(r: any): boolean {
    if (this.swapDisabled) return true;
    if (!this.isExecutableRoute(r)) return true;
    if (!this.validateAlternative) return false;
    return this.validateAlternative(r).disabled;
  }

  // ✅ au clic : on mémorise la sélection puis on émet vers le parent
 onValidateAlternative(route: any) {
const label = this.getRouteProvider(route);
const key   = this.getProviderKey(route);
this.selectedAltKey = key;

console.log(
  '[ALT][CLICK] label=%s | key=%s | isAltSelected=%s | state={selectedAltKey:%s,currentQuoteKey:%s} | raw={providerName:%s,providerKey:%s,toolKey:%s,tool:%s,source:%s}',
  label, key,
  this.isAltSelected(route),
  this.selectedAltKey, this.currentQuoteKey,
  route?.providerName, route?.providerKey, route?.toolKey, route?.tool, route?.source
);
  this.selectAlternative.emit(route);               // le parent exécute/re-quote
  // this.showAlternatives = false; // option: refermer le tableau
}
  /* ------------------------ DEX / logos ------------------------ */
  get swapIconUrl(): string | null {
    const toolLogoURI = this.quote?.ToolLogoURI ?? this.quote?.toolLogoURI ?? null;
    const legacyIcon  = this.quote?.Icon ?? this.quote?.icon ?? null;
    const lifiLogo    = this.quote?.toolDetails?.logoURI ?? null;
    return toolLogoURI || legacyIcon || lifiLogo || null;
  }

get dexSource(): string {
  const raw =
    this.quote?.toolKey ||
    this.quote?.toolName ||
    this.quote?.Source ||
    this.quote?.source ||
    '—';

  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

  /* ------------------------ Status helpers ------------------------ */
  isUserCancelled(err: any): boolean {
    if (!err) return false;
    if (typeof err === 'string') {
      const s = err.toLowerCase();
      return (
        s.includes('action_rejected') || s.includes('user denied') || s.includes('user rejected') ||
        s.includes('rejected by user') || s.includes('request rejected') || s.includes('cancelled') ||
        s.includes('canceled') || s.includes('code=4001')
      );
    }
    const code =
      err?.code ?? err?.error?.code ?? err?.data?.code ?? err?.info?.error?.code ?? err?.cause?.code;
    const r = String(
      err?.reason || err?.shortMessage || err?.error?.message || err?.info?.error?.message ||
      err?.data?.message || err?.message || ''
    ).toLowerCase();

    return (
      code === 4001 || code === 'ACTION_REJECTED' ||
      r.includes('action rejected') || r.includes('user denied') || r.includes('user rejected') ||
      r.includes('rejected by user') || r.includes('request rejected') ||
      r.includes('cancelled') || r.includes('canceled')
    );
  }

  /* ------------------------ Formats / montants ------------------------ */
  private nfCache = new Map<string, Intl.NumberFormat>();
  formatAmount(raw: string | number | bigint, decimals = 18): string {
    if (raw === '' || raw === null || raw === undefined) return '';
    const valueNum = Number(raw.toString());
    if (!isFinite(valueNum)) return '';

    let min = 2, max = 2;
    const abs = Math.abs(valueNum);
    if (abs >= 100) max = 2;
    else if (abs >= 1) max = 4;
    else if (abs >= 0.01) max = 6;
    else { min = 4; max = Math.max(min, Math.min(decimals, 8)); }

    const key = `${min}-${max}`;
    if (!this.nfCache.has(key)) {
      this.nfCache.set(key, new Intl.NumberFormat('en-US', { minimumFractionDigits: min, maximumFractionDigits: max }));
    }
    return this.nfCache.get(key)!.format(valueNum);
  }

  private toNumberSafe(text?: string): number {
    const n = Number((text ?? '').toString().replace(',', '.'));
    return isNaN(n) ? 0 : n;
  }

  private getLocaleForCurrency(code: string): string {
    const map: Record<string, string> = { EUR: 'fr-FR', USD: 'en-US', XOF: 'fr-FR', CFA: 'fr-FR', BTC: 'en-US', ETH: 'en-US' };
    return map[code.toUpperCase()] || navigator.language || 'en-US';
  }

  getRouteLifiFee(r: any): { textEth: string; textFiat: string } {
    const toNum = (v: any) => (typeof v === 'bigint' ? Number(v) : Number(v ?? 0));

    // 1) Montant natif (wei-like) : LiFi direct, sinon price * limit
    let feeWei = toNum(r?.lifiGasAmountWei ?? r?.LifiGasAmountWei ?? r?.LifiNetworkFeeWei);
    if (!feeWei) {
      const priceWei = toNum(r?.lifiGasPriceWei ?? r?.LifiGasPriceWei);
      const gasLimit = toNum(r?.lifiEstimatedGas ?? r?.LifiEstimatedGas);
      if (priceWei && gasLimit) feeWei = priceWei * gasLimit;
    }

    // 2) Métadonnées du token gas (venant du backend)
    const rawSym = (r?.GasTokenSymbol ?? r?.gasTokenSymbol) || 'ETH';
    const symbol = this.canonicalNativeSymbol(Number(this.quote?.fromChainId), rawSym);
    const decimals = Number(r?.GasTokenDecimals ?? r?.gasTokenDecimals ?? 18) || 18;
    const pow10 = Math.pow(10, decimals);

    // 3) Affichage du montant natif avec le bon symbole
    const feeNative = feeWei ? feeWei / pow10 : 0;
    const NATIVE_MIN = 1e-8;
    let textEth = '';
    if (feeNative > 0) {
      textEth = feeNative >= NATIVE_MIN
        ? `${feeNative.toFixed(8)} ${symbol}`
        : `<${NATIVE_MIN.toFixed(8)} ${symbol}`;
    }

    // 4) USD / Fiat : total USD LiFi, sinon feeNative × prixUSD du token gas
    let usd = toNum(r?.lifiGasFeeUsd ?? r?.LifiGasFeeUsd);
    if (!usd && feeWei) {
      const usdPerNative =
        toNum(r?.lifiGasTokenPriceUsd ?? r?.LifiGasTokenPriceUsd) ||
        toNum(r?.GasTokenPriceUsd ?? r?.gasTokenPriceUsd) ||
        (this.nativeTokenUsd || 0);
      if (usdPerNative) usd = feeNative * usdPerNative;
    }

    const rate = this.fiatRates?.[this.fiatCode] || 1;
    const fiat = usd * rate;
    const FIAT_MIN = 0.01;
    let textFiat = '';
    if (fiat > 0) {
      textFiat = fiat >= FIAT_MIN
        ? '≈ ' + fiat.toLocaleString(this.fiatCode === 'EUR' ? 'fr-FR' : 'en-US', { style: 'currency', currency: this.fiatCode })
        : '≈ < ' + FIAT_MIN.toLocaleString(this.fiatCode === 'EUR' ? 'fr-FR' : 'en-US', { style: 'currency', currency: this.fiatCode });
    }

    return { textEth, textFiat };
  }




  // ✅ MM-like ensuite (utilise tes nouveaux champs mm*)
  getRouteMmFee(r: any): { textEth: string; textFiat: string } {
    const toNum = (v: any) => (typeof v === 'bigint' ? Number(v) : Number(v ?? 0));

    let feeWei = toNum(r?.mmExpectedNetworkFeeWei ?? r?.mmMaxNetworkFeeWei);
    const feeEth = feeWei ? feeWei / 1e18 : 0;

    const chainId = Number(this.quote?.fromChainId ?? r?.fromChainId ?? r?.chainId);
    const symbol = this.canonicalNativeSymbol(chainId);
    const textEth = feeEth ? `${feeEth.toFixed(8)} ${symbol}` : '';

    const gasUsd = toNum(this.quote?.gasTokenPriceUsd ?? this.quote?.GasTokenPriceUsd) || (this.nativeTokenUsd || 0);
    const usd = gasUsd ? feeEth * gasUsd : 0;

    const rate = this.fiatRates?.[this.fiatCode] || 1;
    const textFiat = usd
      ? '≈ ' + (usd * rate).toLocaleString(
        this.fiatCode === 'EUR' ? 'fr-FR' : 'en-US',
        { style: 'currency', currency: this.fiatCode }
      )
      : '';

    return { textEth, textFiat };
  }



  get displayedSellAmount(): number { return this.toNumberSafe(this.quote?.formattedSellAmount); }
  get displayedBuyAmount(): number  { return this.toNumberSafe(this.quote?.formattedBuyAmount); }

  getSellAmountFiat(): string {
    const priceUsd = this.toNumberSafe(this.quote?.sellTokenUsdPrice);
    const amount   = this.toNumberSafe(this.quote?.formattedSellAmount);
    const rate     = this.fiatRates?.[this.fiatCode] || 1;
    const value    = priceUsd * amount * rate;
    if (!value) return '';
    return value.toLocaleString(this.getLocaleForCurrency(this.fiatCode),
      { style: 'currency', currency: this.fiatCode, minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  getBuyAmountFiat(): string {
    const priceUsd = this.toNumberSafe(this.quote?.buyTokenUsdPrice);
    const amount   = this.toNumberSafe(this.quote?.formattedBuyAmount);
    const rate     = this.fiatRates?.[this.fiatCode] || 1;
    const value    = priceUsd * amount * rate;
    if (!value) return '';
    return value.toLocaleString(this.getLocaleForCurrency(this.fiatCode),
      { style: 'currency', currency: this.fiatCode, minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  /* -- Gas (principal / courante) -- */
  // Prix du gas en Gwei (prend l’@Input MetaMask s’il existe)
  get gasPriceGweiNumber(): number {
    const fromInput = this.gasPriceWei ? Number(this.gasPriceWei) / 1e9 : 0;
    if (fromInput > 0) return fromInput;

    const fromQuote = Number(this.quote?.gasPriceWei ?? this.quote?.GasPriceWei ?? 0) / 1e9;
    return fromQuote || 0;
  }

  get displayedGasPriceGwei(): string {
    return this.gasPriceGweiNumber.toFixed(2);
  }

  // Units de gas utilisés (best effort depuis la quote)
  get estimatedGasUsed(): number {
    // prend EstimatedGas (backend) sinon estimate LiFi
    const v = BigInt(String(
      this.quote?.estimatedGas ?? this.quote?.EstimatedGas ?? 0
    ));
    return Number(v);
  }
  // —— Helpers d’affichage des 3 variantes de frais réseau ——

// Récupère le symbole natif du réseau (ajuste ta map si besoin)
private nativeSymbolByChain(chainId?: number): string {
  const m: Record<number, string> = { 1:'ETH', 10:'ETH', 56:'BNB', 137:'MATIC', 8453:'ETH' };
  return chainId ? (m[chainId] ?? 'ETH') : 'ETH';
}

get lifiFeeRow() {
  const sym = this.nativeSymbolByChain(Number(this.quote?.fromChainId));
  const native = Number((this.quote as any)?.lifiNetworkFeeNative ?? 0);
  const usd = Number((this.quote as any)?.lifiNetworkFeeUsd ?? 0);
  return {
    has: native > 0,
    textEth: `${this.formatAmount(native, 6)} ${sym}`,
    textFiat: usd > 0 ? `≈ ${this.formatAmount(usd, 2)} ${this.fiatCode}` : undefined
  };
}

get mmExpectedFeeRow() {
  const sym = this.nativeSymbolByChain(Number(this.quote?.fromChainId));
  const native = Number((this.quote as any)?.mmExpectedNetworkFeeNative ?? 0);
  const usd = Number((this.quote as any)?.mmExpectedNetworkFeeUsd ?? 0);
  return {
    has: native > 0,
    textEth: `${this.formatAmount(native, 6)} ${sym}`,
    textFiat: usd > 0 ? `≈ ${this.formatAmount(usd, 2)} ${this.fiatCode}` : undefined
  };
}

get mmMaxFeeRow() {
  const sym = this.nativeSymbolByChain(Number(this.quote?.fromChainId));
  const native = Number((this.quote as any)?.mmMaxNetworkFeeNative ?? 0);
  const usd = Number((this.quote as any)?.mmMaxNetworkFeeUsd ?? 0);
  return {
    has: native > 0,
    textEth: `${this.formatAmount(native, 6)} ${sym}`,
    textFiat: usd > 0 ? `≈ ${this.formatAmount(usd, 2)} ${this.fiatCode}` : undefined
  };
}

  // Montant des frais réseau (ETH) — priorité à fullGasWei injecté (MetaMask)
  get displayedGasFee(): string {
    const nat = this.canonicalNativeSymbol(Number(this.quote?.fromChainId));

    const totalWei =
      (this.fullGasWei && this.fullGasWei > 0n)
        ? this.fullGasWei
        : (this._fallbackFullGasWei && this._fallbackFullGasWei > 0n)
          ? this._fallbackFullGasWei
          : BigInt(Math.max(0, Math.floor(this.estimatedGasUsed))) *
          BigInt(Math.floor(this.gasPriceGweiNumber * 1e9));

    const eth = Number(totalWei) / 1e18;
    return `${eth.toFixed(6)} ${nat}`;
  }


  // Fiat ≈ totalWei × nativeTokenUsd (si dispo)
  // Fiat ≈ totalWei × nativeTokenUsd (si dispo)
  getGasFeeFiat(): string | null {
    const totalWei =
      (this.fullGasWei && this.fullGasWei > 0n)
        ? this.fullGasWei
        : (this._fallbackFullGasWei && this._fallbackFullGasWei > 0n)
          ? this._fallbackFullGasWei
          : BigInt(Math.max(0, Math.floor(this.estimatedGasUsed))) *
          BigInt(Math.floor(this.gasPriceGweiNumber * 1e9));

    if (!this.nativeTokenUsd || totalWei <= 0n) return null;

    const rate = this.fiatRates?.[this.fiatCode] || 1;
    const eth = Number(totalWei) / 1e18;
    const usd = eth * this.nativeTokenUsd;
    const fiat = usd * rate;

    return (Math.round(fiat * 100) / 100).toLocaleString(
      this.fiatCode === 'EUR' ? 'fr-FR' : 'en-US',
      { style: 'currency', currency: this.fiatCode, minimumFractionDigits: 2, maximumFractionDigits: 2 }
    );
  }





  getTokenLabel(symbol: string): string {
    const key = 'TOKENS.' + symbol.toUpperCase();
    const translated = this.translate.instant(key);
    return translated !== key ? translated : symbol;
  }


  private feeWeiOf(route: any): bigint {
    if (!route) return 0n;
    const ga = route?.gasAmountWei ?? route?.GasAmountWei;
    if (ga != null) {
      try { return BigInt(ga); } catch { /* ignore */ }
    }
    const gp = route?.gasPriceWei ?? route?.GasPriceWei;
    const gl = route?.estimatedGas ?? route?.EstimatedGas;
    if (gp != null && gl != null) {
      try { return BigInt(gp) * BigInt(gl); } catch { /* ignore */ }
    }
    return 0n;
  }

  /** Prix USD du token gas pour une route (fallback sur la quote si absent dans la route) */
  private gasUsdOf(route: any): number {
    const fromRoute =
      Number(route?.gasTokenPriceUsd ?? route?.GasTokenPriceUsd ?? 0);
    if (fromRoute > 0) return fromRoute;

    const fromMain =
      Number(this.quote?.gasTokenPriceUsd ?? this.quote?.GasTokenPriceUsd ?? 0);
    return fromMain > 0 ? fromMain : 0;
  }

  /** Total network fee en USD pour une route */
  private feeUsdOf(route: any): number {
    const wei = this.feeWeiOf(route);
    const usd = this.gasUsdOf(route);
    if (!usd || wei === 0n) return 0;
    const eth = Number(wei) / 1e18;
    return eth * usd;
  }

  /** Liste des routes alternatives à comparer (normalisées) */
  private get altRoutes(): any[] {
    const explicit = Array.isArray(this.otherRoutes) ? this.otherRoutes : null;
    if (explicit && explicit.length) return explicit;

    const inQuote = Array.isArray(this.quote?.singleRoutes)
      ? this.quote.singleRoutes
      : [];
    return inQuote;
  }

  // ---------- Exposition “Gain sur frais réseau” ----------
  /** Worst fee (USD) parmi les alternatives, pour comparer au “best” (la route principale sélectionnée) */
  private get worstAltFeeUsd(): number {
    const fees = this.altRoutes
      .filter(r => !!r && !this.isSameProvider(r, this.quote))
      .map(r => this.feeUsdOf(r))
      .filter(v => isFinite(v) && v > 0);
    if (!fees.length) return 0;
    return Math.max(...fees);
  }

  /** Best fee (USD) = la route sélectionnée (car ton écran présente déjà la meilleure) */
  private get bestMainFeeUsd(): number {
    return this.feeUsdOf(this.quote);
  }

  /** Gain absolu en USD (worst - best), clampé à >= 0 */
  get networkFeeGainAbs(): number {
    const worst = this.worstAltFeeUsd;
    const best = this.bestMainFeeUsd;
    const gain = worst - best;
    return gain > 0 ? gain : 0;
    // Si tu veux aussi exposer le gain en ETH:
    // const gainEth = (gain / (this.gasUsdOf(this.quote) || 1));
  }

  /** Gain en % vs la pire route (base 100 = pire) */
  get networkFeeGainPct(): number {
    const worst = this.worstAltFeeUsd;
    if (worst <= 0) return 0;
    return (this.networkFeeGainAbs / worst) * 100;
  }

  
  /* ------------------------ ALTERNATIVES ------------------------ */

  // toutes les routes single-step remontées par le backend
  private get allSingleRoutes(): any[] {
    // attendu côté back: quote.singleRoutes: Array<...>
    return Array.isArray(this.quote?.singleRoutes) ? this.quote.singleRoutes : [];
  }





  // Ajoute dans la classe QuotePreviewComponent

// Indique si la perte est critique
get isCriticalLoss(): boolean {
  return false; // stub, à remplacer par ton calcul réel
}

// Valeur fiat de la perte nette
get netLossFiat(): number {
  return 0; // stub
}

// Formatte une erreur swap pour l’affichage
getFormattedSwapError(err: any): string {
  if (!err) return '';
  if (typeof err === 'string') return err;
  if (err?.message) return err.message;
  return JSON.stringify(err);
}


/** --------- ALTERNATIVES: helpers robustes --------- */

// Logo du provider (prend ce qui vient du back en priorité)
getRouteLogo(r: any): string | null {
  return (
    r?.providerLogo ||
    r?.ToolLogoURI ||
    r?.toolLogoURI ||
    r?.logoURI ||
    r?.logo ||
    r?.Icon ||
    r?.icon ||
    r?.toolDetails?.logoURI ||
    null
  );
}

// Nom/clé du provider
getRouteProvider(x: any): string {
  return (x?.providerName ?? x?.ProviderName) || this.getProviderKey(x);
}

// Symbole du token reçu
getRouteBuySymbol(r: any): string {
  return (
    r?.buyTokenSymbol ||
    r?.toTokenSymbol ||
    r?.action?.toToken?.symbol ||
    this.quote?.buyTokenSymbol ||
    this.quote?.BuyTokenSymbol ||
    '—'
  );
}

// Montant reçu estimé (formaté)
// 1) utilise une valeur déjà formatée si dispo
// 2) sinon calcule depuis toAmountRaw et les décimales
getRouteEstimatedBuy(r: any): string {
  if (r?.formattedBuyAmount) {
    return `${this.formatAmount(r.formattedBuyAmount, 8)} ${this.getRouteBuySymbol(r)}`;
  }

  const raw = Number(r?.toAmountRaw ?? r?.ToAmountRaw ?? r?.toAmount ?? 0);
  if (!raw) return '—';

  const dec =
    Number(
      r?.toTokenDecimals ??
      r?.toToken?.decimals ??
      this.quote?.buyTokenDecimals ??
      this.quote?.BuyTokenDecimals ??
      18
    );

  const val = raw / Math.pow(10, dec);
  return `${this.formatAmount(val, 8)} ${this.getRouteBuySymbol(r)}`;
}

// Reconnaître la route “courante” pour ne pas la ré-afficher
isSameProvider(a: any, b: any): boolean {
  return this.getProviderKey(a) === this.getProviderKey(b);
}



// Les routes alternatives à afficher
get otherRoutes(): any[] {
  const all = Array.isArray(this.quote?.singleRoutes) ? this.quote!.singleRoutes : [];
  const current = this.quote || {};
 return all.filter((r: any) => !this.isSameProvider(r, current));
}



  /* ------------------------ Divers UI existants ------------------------ */

  onShareClick(): void {
    const baseUrl = window.location.origin + '/';
    const tokenIn = this.sellTokenSymbol;
    const tokenOut = this.buyTokenSymbol;
    const amountIn = this.quote?.formattedSellAmount;
    const shareUrl = `${baseUrl}?tokenIn=${tokenIn}&tokenOut=${tokenOut}&amountIn=${amountIn}`;

    if (navigator.share) {
      navigator.share({ title: 'SmartSwap', text: this.translate.instant('DEFI_OPPORTUNITY'), url: shareUrl })
        .catch(err => console.error('❌ Erreur partage', err));
    } else {
      navigator.clipboard.writeText(shareUrl).then(() => {
        this.showToast = true;
        setTimeout(() => this.showToast = false, 3000);
      });
    }
  }
  // Fiat du montant reçu estimé pour une route alternative
  getRouteEstimatedBuyFiat(r: any): string {
    // 1) si le back fournit déjà l’USD, on le prend
    let usd: number | null = null;
    if (r?.toAmountUsd != null) {
      const n = Number(r.toAmountUsd);
      usd = isNaN(n) ? null : n;
    }

    // 2) sinon on calcule: amount * buyTokenUsdPrice
    if (usd == null) {
      // amount
      let amount: number | null = null;
      if (r?.formattedBuyAmount != null) {
        const n = Number(r.formattedBuyAmount);
        amount = isNaN(n) ? null : n;
      } else {
        const raw = Number(r?.toAmountRaw ?? r?.ToAmountRaw ?? r?.toAmount ?? 0);
        const dec =
          Number(
            r?.toTokenDecimals ??
            r?.toToken?.decimals ??
            this.quote?.buyTokenDecimals ??
            this.quote?.BuyTokenDecimals ??
            18
          );
        if (raw) amount = raw / Math.pow(10, dec);
      }

      // prix USD
      const price =
        r?.buyTokenUsdPrice != null
          ? Number(r.buyTokenUsdPrice)
          : this.quote?.buyTokenUsdPrice != null
            ? Number(this.quote.buyTokenUsdPrice)
            : NaN;

      if (amount != null && !isNaN(price)) {
        usd = amount * price;
      }
    }

    if (usd == null) return '';

    const rate = this.fiatRates?.[this.fiatCode] || 1;
    const value = usd * rate;
    return (
      '≈ ' +
      value.toLocaleString(
        this.fiatCode === 'EUR' ? 'fr-FR' : 'en-US',
        { style: 'currency', currency: this.fiatCode }
      )
    );
  }

  get formattedSource(): string {
    return this.quote?.source || 'Inconnu';
  }

  getNetworkLabel(): string {
    const id = this.quote?.chainId ?? this.quote?.fromChainId ?? this.quote?.action?.fromChainId ?? this.quote?.estimate?.fromChainId ?? null;
    if (!id) return '';
    const names: Record<number, string> = {
      1: 'Ethereum', 10: 'Optimism', 56: 'BNB Chain', 100: 'Gnosis',
      137: 'Polygon', 1101: 'Polygon zkEVM', 250: 'Fantom', 324: 'zkSync Era',
      8453: 'Base', 42161: 'Arbitrum', 43114: 'Avalanche', 59144: 'Linea'
    };
    return names[id] ?? `Chain #${id}`;
  }
}
